import type { FC } from 'react';
import { Fragment } from 'react';

import { useCms, useContent, useImageMapper, useSetting } from '@eneco/cms';
import logger from '@eneco/common/log';
import { useChat } from '@eneco/components/chat/useChat';
import RichTextComponent from '@eneco/components/RichText/RichText';
import { StatusIndicator } from '@eneco/components/ui/StatusIndicator/StatusIndicator';
import { Badge, Box, Button, ButtonLink, Card, Divider, Expandable, Grid, Heading, Image, Stack, Stretch, Text, TextLink } from '@eneco/sparky';
import { MobileIcon } from '@eneco/sparky/icons';
import type { FeedbackColors, HeadingProps } from '@eneco/sparky/types';

import type { ContactCustomerServiceChannel, ContactCustomerServiceContent, ContactCustomerServiceGroup, ContactCustomerServiceItem } from './contact-customer-service-content.types';

type ColorTypes = 'success' | 'warning' | 'error';

const headingChild = (level: HeadingProps['as'], by: number, cap: number): HeadingProps['as'] =>
  `h${Math.min(cap, Number(level.charAt(1)) + by)}` as HeadingProps['as'];

const ChatButton: FC<{ chatLink: ContactCustomerServiceChannel }> = ({ chatLink }) => {
  try {
    const [mount] = useChat();
    return (
      <Button action="primary" size="regular" tone="onLight" type="button" onClick={() => mount({ visibility: 'open', topic: chatLink.chatTopic })}>
        {chatLink.displayText}
      </Button>
    );
    // Safeguard against accidental usage outside ChatProvider
  } catch (e) {
    logger.error('NtdDKV', e instanceof Error ? e.message : 'ChatButton could not render');
    return null;
  }
};

const ContactExpandableTrigger: FC<{
  title: string;
  availabilityColor: Extract<keyof FeedbackColors, 'feedbackSuccess' | 'feedbackWarning' | 'feedbackError'>;
  availabilityText?: string;
  headingLevel: HeadingProps['as'];
  isBadge: boolean;
  openingHoursAvailabilityText?: string;
}> = ({ title, availabilityColor, availabilityText, headingLevel, isBadge, openingHoursAvailabilityText }) => (
  <Expandable.Trigger label={title}>
    <Stack gap="2">
      <Heading as={headingLevel} size={{ initial: '3XS', md: 'XS' }}>
        {title}
      </Heading>
      {!isBadge && availabilityText && <StatusIndicator color={availabilityColor}>{availabilityText}</StatusIndicator>}
      {isBadge && openingHoursAvailabilityText && <Text as="p">{openingHoursAvailabilityText}</Text>}
    </Stack>
  </Expandable.Trigger>
);

const ContactExpandableContent: FC<{
  items?: ContactCustomerServiceItem[];
  openingHoursTitle?: string;
  openingHoursContent?: unknown;
  isBadge: boolean;
  compactView: boolean;
  headingLevel: HeadingProps['as'];
}> = ({ items, headingLevel, openingHoursTitle, openingHoursContent, isBadge, compactView }) => {
  const { adapter } = useCms();
  const openingHoursHtml = adapter.toRichText(openingHoursContent);

  return (
    <Expandable.Content>
      <Box paddingTop={!isBadge ? '12' : '6'} paddingBottom="4">
        <Stack gap="6">
          <Stack>
            {items?.map((item, index) => {
              const html = adapter.toRichText(item.content);
              const buttonLink = adapter.toLink(item.buttonLink);
              const link = adapter.toLink(item.link);

              return (
                <Fragment key={item._key ?? String(index)}>
                  <Stack gap="2">
                    {item.title && (
                      <Heading as={headingLevel} size={{ initial: '3XS', md: '2XS' }}>
                        {item.title}
                      </Heading>
                    )}
                    {html && (
                      <Text>
                        <RichTextComponent html={html} />
                      </Text>
                    )}
                    <Box paddingTop={compactView ? '0' : '4'} paddingBottom={items && index !== items.length - 1 ? '6' : '0'}>
                      <Stack gap="4" alignX="start">
                        {item.chatLink?.chatTopic && item.chatLink?.displayText && <ChatButton chatLink={item.chatLink} />}
                        {buttonLink?.text && <ButtonLink href={buttonLink.href}>{buttonLink.text}</ButtonLink>}
                        {link?.text && (
                          <TextLink target={link.target} href={link.href} emphasis="high">
                            {link.text}
                          </TextLink>
                        )}
                        {item.phoneNumber && (
                          <Stack direction="row" gap="1">
                            <MobileIcon />
                            <Text size={{ initial: 'BodyM', md: 'BodyL' }} weight="bold">
                              <TextLink href={`tel:${item.phoneNumber}`} emphasis="medium">
                                {item.phoneNumber}
                              </TextLink>
                            </Text>
                          </Stack>
                        )}
                      </Stack>
                    </Box>
                  </Stack>

                  {items && items.length - 1 !== index && !compactView ? (
                    <Box paddingTop="2" paddingBottom="8">
                      <Divider />
                    </Box>
                  ) : null}
                </Fragment>
              );
            })}
          </Stack>

          {openingHoursTitle && openingHoursHtml && (
            <Stack gap="2">
              <Heading as={headingLevel} size={{ initial: '3XS', md: '2XS' }}>
                {openingHoursTitle}
              </Heading>
              <Text>{openingHoursHtml ? <RichTextComponent html={openingHoursHtml} /> : null}</Text>
            </Stack>
          )}
        </Stack>
      </Box>
    </Expandable.Content>
  );
};

const ContactExpandable: FC<{ item: ContactCustomerServiceGroup; headingLevel: HeadingProps['as']; isBadge: boolean; compactView: boolean }> = ({
  item,
  headingLevel,
  isBadge,
  compactView,
}) => {
  const triggerHeadingLevel = headingChild(headingLevel, 1, 5);
  const contentHeadingLevel = headingChild(headingLevel, 2, 6);

  return (
    <Expandable defaultOpen={item.startsExpanded ?? false}>
      <Stack>
        <ContactExpandableTrigger
          title={item.title ?? ''}
          availabilityColor={(item.availabilityColor as Extract<keyof FeedbackColors, 'feedbackSuccess' | 'feedbackWarning' | 'feedbackError'>) || 'feedbackError'}
          availabilityText={item.availabilityText}
          headingLevel={triggerHeadingLevel}
          isBadge={isBadge}
          openingHoursAvailabilityText={item.openingHoursAvailabilityText}
        />
        <ContactExpandableContent
          headingLevel={contentHeadingLevel}
          items={item.items}
          openingHoursTitle={item.openingHoursTitle}
          openingHoursContent={item.openingHoursContent}
          isBadge={isBadge}
          compactView={compactView}
        />
      </Stack>
    </Expandable>
  );
};

/**
 * `ContactCustomerService` block for the CMS-agnostic lane, serving **both** providers.
 *
 * Mirrors the legacy component (`libs/sitecore/components/content/src/ContactCustomerService/
 * ContactCustomerService.tsx`): a title above a `Card` of expandable contact-method groups, each with an
 * availability badge/indicator, one or more contact channels (chat, button link, text link, phone), and
 * an optional opening-hours note; an optional image renders alongside on desktop.
 *
 * Known simplification: see `contact-customer-service-content.types.ts` — the generated Sanity schema
 * has no `items` field at all yet, so the contact-method groups cannot render on `?_cms=sanity` until
 * `migrator schema-gen` is re-run; only the title and image show today.
 */
const ContactCustomerService: FC = () => {
  const content = useContent<ContactCustomerServiceContent>();
  const mapImage = useImageMapper();

  const headingLevel = headingChild((useSetting('headingLevel') || 'h3') as HeadingProps['as'], -1, 4);
  // Sitecore's Rendering Parameters Template names these `RenderVariant`/`CompactViewmode` (PascalCase);
  // the Sanity schema lowercases them. `useSetting` does an exact-string lookup with no case-folding
  // (confirmed), so both names need reading — the Sitecore lane's own boolean-ish param also serializes
  // as `'1'`, not `'true'`.
  const renderVariantSanity = useSetting('renderVariant');
  const renderVariantSitecore = useSetting('RenderVariant');
  const isBadge = (renderVariantSanity ?? renderVariantSitecore) === 'Badge';
  const compactViewmodeSanity = useSetting('compactViewmode');
  const compactViewmodeSitecore = useSetting('CompactViewmode');
  const compactViewmode = compactViewmodeSanity ?? compactViewmodeSitecore;
  const compactView = compactViewmode === 'true' || compactViewmode === '1';

  const imageProps = mapImage(content.image);
  const hasImage = Boolean(imageProps?.src);
  const gridItemColumn = hasImage ? { initial: '1/-1', md: '1/2' } : { initial: '1/-1', md: '1/-1', lg: '2/4' };

  const items = content.items ?? [];

  return (
    <Grid gap="6" columns={hasImage ? 2 : 4}>
      <Grid.Item gridColumn={gridItemColumn}>
        <Box paddingTop={{ initial: '8', md: '16' }} paddingBottom={{ md: '16' }}>
          <Box paddingBottom={{ initial: '6', md: '8' }}>
            <Heading as={headingLevel} size={{ initial: 'XS', md: 'M' }} color="inherit">
              {content.title}
            </Heading>
          </Box>
          <Card>
            {items.map((item, index) => {
              let badgeColor: ColorTypes = 'success';
              switch (item.availabilityColor) {
                case 'feedbackBackgroundWarning':
                  badgeColor = 'warning';
                  break;
                case 'feedbackBackgroundError':
                  badgeColor = 'error';
                  break;
              }
              return (
                <Fragment key={item._key ?? String(index)}>
                  <Box paddingX="8" paddingY="6">
                    <Stack gap="4">
                      {isBadge && item.availabilityText && (
                        <Box>
                          <Badge size="M" color={badgeColor}>
                            {item.availabilityText}
                          </Badge>
                        </Box>
                      )}
                      <ContactExpandable item={item} headingLevel={headingLevel} isBadge={isBadge} compactView={compactView} />
                    </Stack>
                  </Box>
                  {items.length - 1 !== index ? <Divider /> : null}
                </Fragment>
              );
            })}
          </Card>
        </Box>
      </Grid.Item>
      {hasImage && imageProps && (
        <Grid.Item gridColumn={{ initial: '1/-1', md: '2/3' }}>
          <Stretch>
            <Box>
              <Stretch>
                <Stack alignX={{ initial: 'center', md: 'end' }}>
                  <Image src={imageProps.src} alt={imageProps.alt ?? ''} maxWidth="100%" />
                </Stack>
              </Stretch>
            </Box>
          </Stretch>
        </Grid.Item>
      )}
    </Grid>
  );
};

export default ContactCustomerService;
